import React from 'react';
import {
    makeStyles,
    tokens,
    Button,
    Menu,
    MenuTrigger,
    MenuPopover,
    mergeClasses,
    type MenuOpenChangeData,
    type MenuOpenEvent,
} from '@fluentui/react-components';
import { observer } from 'owa-mobx-react';
import { isFeatureEnabled } from 'owa-feature-flags';
import fileButtonStrings from './fileButton.locstring.json';
import loc from 'owa-localize';

interface FileButtonProps {
    menuContent: React.ReactNode;
    label?: string;
}

export const FileButton: React.FunctionComponent<FileButtonProps> = observer(
    ({ menuContent, label }) => {
        const styles = useStyles();
        const fileLabel = label || loc(fileButtonStrings.FileButton);
        const [open, setOpen] = React.useState(false);
        const buttonRef = React.useRef<HTMLButtonElement>(null);

        const handleOpenChange = React.useCallback(
            (event: MenuOpenEvent, data: MenuOpenChangeData) => {
                if (!data.open) {
                    event.stopPropagation();
                }
                setOpen(data.open);
            },
            []
        );

        const handleKeyDown = React.useCallback(
            (event: React.KeyboardEvent) => {
                if (event.key === 'Escape' && !open) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            },
            [open]
        );

        if (!isFeatureEnabled('mon-ribbon-file-tab')) {
            return null;
        }
        const menu = (
            <Menu open={open} onOpenChange={handleOpenChange}>
                <MenuTrigger>
                    <Button
                        appearance="transparent"
                        className={mergeClasses(styles.button, open && styles.selected)}
                        ref={buttonRef}
                    >
                        {fileLabel}
                    </Button>
                </MenuTrigger>
                <MenuPopover>{menuContent}</MenuPopover>
            </Menu>
        );
        return open ? menu : <div onKeyDown={handleKeyDown}>{menu}</div>;
    },
    'FileButton'
);

const useStyles = makeStyles({
    button: {
        fontWeight: tokens.fontWeightRegular,
        height: '32px',
        marginLeft: '3px',
        marginRight: '2px',
        minWidth: 'auto',
        padding: `0 ${tokens.spacingHorizontalMNudge}`,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground2Hover,
            color: tokens.colorNeutralForeground1,
        },
    },
    selected: {
        backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
});
